import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Star } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";

const DeliveryFeedback = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [order, setOrder] = useState(null);
  const [reviews, setReviews] = useState({});
  const [invalid, setInvalid] = useState(!token);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  useEffect(() => {
    if (!token) return;
    api.get("/feedback/order", { params: { token } })
      .then((response) => {
        setOrder(response.data);
        setReviews(Object.fromEntries(response.data.items.map((item) => [
          item.line_key,
          { stars: 0, review: "" },
        ])));
      })
      .catch(() => setInvalid(true));
  }, [token]);

  const updateReview = (lineKey, patch) => {
    setReviews((current) => ({
      ...current,
      [lineKey]: { ...current[lineKey], ...patch },
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!order || order.items.some((item) => !reviews[item.line_key]?.stars)) {
      toast.error("Please choose a rating for every product.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/feedback/submit", {
        token,
        reviews: order.items.map((item) => ({
          line_key: item.line_key,
          stars: reviews[item.line_key].stars,
          review: reviews[item.line_key].review,
        })),
      });
      setSuccess(data);
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSubmitting(false);
    }
  };

  if (invalid) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center" data-testid="feedback-invalid-page">
        <h1 className="font-serif text-4xl font-semibold">Product review link unavailable</h1>
        <p className="mt-3 text-[#64748B]">Please use the private link from your Eira email.</p>
      </div>
    );
  }

  if (success) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20" data-testid="feedback-success-page">
        <div className="border border-[#E2DCD2] bg-white p-8 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center bg-[#EBF2EE] text-[#2D5A47]">
            <Star size={25} fill="currentColor" />
          </div>
          <h1 className="mt-5 font-serif text-4xl font-semibold">Thank you for your reviews</h1>
          <p className="mt-3 text-[#64748B]" data-testid="feedback-points-awarded">
            {success.points_awarded} Eira Points have been awarded and will be redeemable in 24 hours.
          </p>
        </div>
      </div>
    );
  }

  if (!order) {
    return <div className="py-20 text-center" data-testid="product-feedback-loading">Loading products…</div>;
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-12" data-testid="product-feedback-page">
      <div className="border border-[#E2DCD2] bg-white p-6 sm:p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8C6D46]">
          Eira product reviews
        </p>
        <h1 className="mt-2 font-serif text-4xl font-semibold">How are your products?</h1>
        <p className="mt-3 text-sm leading-6 text-[#64748B]">
          Rate every product from your order. Written reviews are optional.
        </p>

        <form className="mt-8 space-y-6" onSubmit={submit} data-testid="product-feedback-form">
          {order.items.map((item) => {
            const current = reviews[item.line_key] || { stars: 0, review: "" };
            return (
              <section
                key={item.line_key}
                className="border-t border-[#E2DCD2] pt-6 first:border-t-0 first:pt-0"
                data-testid={`product-feedback-item-${item.line_key}`}
              >
                <div className="flex gap-4">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="h-20 w-20 border border-[#E2DCD2] object-cover"
                    data-testid={`product-feedback-image-${item.line_key}`}
                  />
                  <div className="min-w-0 flex-1">
                    <h2 className="font-semibold" data-testid={`product-feedback-name-${item.line_key}`}>
                      {item.name}
                    </h2>
                    <p className="mt-1 text-xs text-[#64748B]">
                      {item.variant_label || "Standard"} · Quantity {item.quantity}
                    </p>
                    <div className="mt-3 flex gap-1" data-testid={`product-feedback-stars-${item.line_key}`}>
                      {[1, 2, 3, 4, 5].map((value) => (
                        <button
                          key={value}
                          type="button"
                          aria-label={`${item.name}: ${value} stars`}
                          onClick={() => updateReview(item.line_key, { stars: value })}
                          className="p-0.5 text-[#D99B26] transition-transform hover:scale-110"
                          data-testid={`product-feedback-star-${item.line_key}-${value}`}
                        >
                          <Star size={26} fill={value <= current.stars ? "currentColor" : "none"} />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <textarea
                  value={current.review}
                  maxLength={2000}
                  onChange={(event) => updateReview(item.line_key, { review: event.target.value })}
                  rows={3}
                  className="mt-4 w-full border border-[#E2DCD2] px-3 py-3 text-sm outline-none focus:border-[#2D5A47]"
                  placeholder={`Write an optional review for ${item.name}`}
                  data-testid={`product-feedback-review-${item.line_key}`}
                />
              </section>
            );
          })}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#C86D42] px-5 py-3.5 text-sm font-semibold text-white disabled:opacity-50"
            data-testid="submit-product-feedback"
          >
            {submitting ? "Submitting reviews…" : "Submit product reviews"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default DeliveryFeedback;